/**
 * The shared primitives. Small on purpose: a component library grows into a
 * second design system, and then nothing matches anything.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

import { t, useUiLang } from "../i18n";

function join(...values: (string | false | undefined)[]): string {
  return values.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "plain" | "solid" | "ghost";
  active?: boolean;
};

export function Button({ variant = "plain", active, className, ...rest }: ButtonProps) {
  const base =
    "tap inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm " +
    "font-medium transition-colors duration-150 disabled:opacity-40 disabled:pointer-events-none";
  const styles = {
    plain: "border border-[var(--color-line)] text-[var(--color-ink)] hover:bg-[var(--color-raised)]",
    solid: "bg-[var(--color-accent)] text-[var(--color-on-accent)] hover:brightness-110",
    ghost: "text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-raised)]",
  }[variant];
  const on = active ? "bg-[var(--color-accent-soft)] text-[var(--color-accent)]" : "";
  return <button className={join(base, styles, on, className)} {...rest} />;
}

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { label: string };

export function IconButton({ label, className, ...rest }: IconButtonProps) {
  return (
    <button
      aria-label={label}
      title={label}
      className={join(
        // `tap` grows the box to 44px under a thumb and leaves it at 36px under
        // a cursor, so the same button suits both without two components.
        "tap inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--color-muted)]",
        "transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]",
        "disabled:opacity-40 disabled:pointer-events-none",
        className,
      )}
      {...rest}
    />
  );
}

export function Chip({
  children,
  active,
  onClick,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={join(
        "inline-flex items-center rounded-full border px-3 py-1 text-xs transition-colors duration-150",
        // A 26px chip is a coin flip with a thumb. Under a coarse pointer it
        // becomes a 44px row; the desktop bar keeps its compact chips.
        "coarse:min-h-11 coarse:px-4 coarse:text-sm",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
          : "border-[var(--color-line)] text-[var(--color-muted)] hover:text-[var(--color-ink)]",
      )}
    >
      {children}
    </button>
  );
}

export function Field({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={join(
        "w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)]",
        "px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-faint)]",
        "coarse:min-h-11",
        "focus:border-[var(--color-accent)] focus:outline-none",
        className,
      )}
      {...rest}
    />
  );
}

export function Spinner({ label }: { label?: string }) {
  useUiLang();
  return (
    <span role="status" aria-label={label ?? t("uiLoading")} className="inline-block">
      <span className="block h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-line)] border-t-[var(--color-accent)]" />
    </span>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-24 text-center">
      <p className="text-sm text-[var(--color-ink)]">{title}</p>
      {hint && <p className="max-w-sm text-xs text-[var(--color-muted)]">{hint}</p>}
    </div>
  );
}

/** Seconds to `1:04:09`, or `4:09` under an hour. */
export function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const parts = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];
  const [hours, minutes, rest] = parts;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`
    : `${minutes}:${String(rest).padStart(2, "0")}`;
}
